<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import { useI18n } from '@/composable/useI18n'
import { useGrafanaStore } from '@/store/grafana'
import { IGrafanaDashboard } from '@/interfaces/api/grafana'

const { t } = useI18n()
const grafanaStore = useGrafanaStore()
const isLoading = ref(true)
const currentIframeUrl = ref<string | null>(null)

onMounted(async () => {
  await grafanaStore.fetchDashboards()
  isLoading.value = false
})

const dashboards = computed(() => grafanaStore.dashboards)

async function openDashboard(dashboard: IGrafanaDashboard) {
  try {
    isLoading.value = true
    const url = await grafanaStore.fetchAccessUrl(dashboard.id)
    if (url) {
      currentIframeUrl.value = url
    }
  } catch (error) {
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

function closeDashboard() {
  currentIframeUrl.value = null
}
</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else-if="currentIframeUrl" class="flex flex-col h-full w-full">
      <div class="px-4 py-4 flex items-center justify-between border-b border-stroke bg-white">
        <div class="flex items-center gap-4">
          <button
            type="button"
            @click="closeDashboard"
            class="flex items-center justify-center p-2 text-slate-700 transition hover:bg-slate-50 border border-stroke"
            :title="t('grafana_dashboards.actions.back')"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="ml-2 font-medium">{{ t('grafana_dashboards.actions.back') }}</span>
          </button>
          <h2 class="text-xl font-semibold text-bodydark2">
            {{ t('grafana_dashboards.panel_title') }}
          </h2>
        </div>
      </div>

      <div class="w-full">
        <iframe
          :src="currentIframeUrl"
          class="w-full border-0 bg-white min-h-[calc(100vh-140px)]"
          allowfullscreen
        ></iframe>
      </div>
    </div>

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'dashboard' }"
        :title="t('grafana_dashboards.title')"
        :subtitle="t('grafana_dashboards.subtitle')"
      />

      <div v-if="dashboards.length > 0" class="mt-4 flex flex-col gap-3">
        <div
          v-for="dashboard in dashboards"
          :key="dashboard.id"
          class="flex items-center justify-between bg-white border border-stroke p-4 shadow-sm hover:bg-slate-50 transition"
        >
          <div>
            <h3 class="font-semibold text-bodydark2">{{ dashboard.title }}</h3>
            <p v-if="dashboard.folderTitle" class="text-sm text-slate-500 mt-1">
              {{ t('grafana_dashboards.list.folder', { folder: dashboard.folderTitle }) }}
            </p>
          </div>

          <button
            type="button"
            class="flex items-center justify-center bg-primary px-4 py-2 text-white transition hover:bg-opacity-90 min-w-[150px]"
            @click="openDashboard(dashboard)"
          >
            {{ t('grafana_dashboards.actions.open_panel') }}
          </button>
        </div>
      </div>

      <div v-else class="flex flex-col items-center justify-center bg-white px-6 py-12 shadow-sm border border-stroke mt-4">
        <div class="text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 class="mt-4 text-lg font-medium text-gray-900">
            {{ t('grafana_dashboards.empty.title') }}
          </h3>
          <p class="mt-2 text-sm text-gray-500">
            {{ t('grafana_dashboards.empty.description') }}
          </p>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>
